import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
    calculateVisibleTicketTotal,
    clearHiddenFormValues,
    getHiddenFormElementIds,
    getVisibleTicketElements,
    isFormElementVisible,
} from '../utils/formVisibility';

const removeKeys = (previousValues, keys) => {
    const nextValues = { ...previousValues };
    let changed = false;
    keys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(nextValues, key)) {
            delete nextValues[key];
            changed = true;
        }
    });
    return changed ? nextValues : previousValues;
};

const useConditionalFormState = ({
    elements,
    formData,
    formValues,
    formValuesLoaded = true,
    scopeKey,
    setFormImagesValues,
    setFormValues,
    setUploadState,
}) => {
    const hiddenImageIdsRef = useRef(new Set());
    const formValuesRef = useRef(formValues);
    formValuesRef.current = formValues;

    const isElementVisible = useCallback(
        (element) => isFormElementVisible(element, formData, formValues),
        [formData, formValues]
    );
    const sanitizeValues = useCallback(
        (values) => clearHiddenFormValues(formData, values),
        [formData]
    );
    const visibleTicketElements = useMemo(
        () => getVisibleTicketElements(formData, formValues),
        [formData, formValues]
    );
    const totalPriceToPay = useMemo(
        () => calculateVisibleTicketTotal(formData, formValues),
        [formData, formValues]
    );

    useEffect(() => {
        hiddenImageIdsRef.current.clear();
    }, [scopeKey]);

    useEffect(() => {
        if (!formValuesLoaded) {
            hiddenImageIdsRef.current.clear();
            return;
        }

        const hiddenElementIds = getHiddenFormElementIds(formData, formValues);
        if (hiddenElementIds.some((id) => Object.prototype.hasOwnProperty.call(formValues || {}, id))) {
            setFormValues(sanitizeValues(formValues));
        }

        const hiddenImageIds = elements
            .filter((element) => element.type === 'image-upload' && hiddenElementIds.includes(element.id))
            .map((element) => element.id);
        if (hiddenImageIds.length === 0) return;

        hiddenImageIds.forEach((id) => hiddenImageIdsRef.current.add(id));
        setFormImagesValues((previousValues) => removeKeys(previousValues, hiddenImageIds));
        setUploadState((previousValues) => removeKeys(previousValues, hiddenImageIds));
    }, [elements, formData, formValues, formValuesLoaded, sanitizeValues, setFormImagesValues, setFormValues, setUploadState]);

    const shouldRequestImage = useCallback((element) => {
        if (!formValuesLoaded) return false;
        if (!isFormElementVisible(element, formData, formValuesRef.current)) {
            hiddenImageIdsRef.current.add(element.id);
            return false;
        }
        return !hiddenImageIdsRef.current.has(element.id);
    }, [formData, formValuesLoaded]);

    const canApplyImage = useCallback((element) =>
        formValuesLoaded
        && !hiddenImageIdsRef.current.has(element.id)
        && isFormElementVisible(element, formData, formValuesRef.current), [formData, formValuesLoaded]);

    return {
        canApplyImage,
        isElementVisible,
        sanitizeValues,
        shouldRequestImage,
        totalPriceToPay,
        visibleTicketElements,
    };
};

export default useConditionalFormState;
